<div class="cb-schema-coverage-canvas">
    <!-- Canvas Info Bar -->
    <div class="canvas-info-bar">
        <i class="fa-regular fa-hand-pointer"></i>
        <span><strong>Click</strong> a module to expand/collapse its entities. <strong>Double-click</strong> an entity to edit its documentation.</span>
        <span class="info-separator">|</span>
        <span><strong>Drag</strong> to pan the canvas. Use <strong>scroll wheel</strong> or buttons to zoom.</span>
    </div>

    <!-- Canvas Toolbar -->
    <div class="canvas-toolbar">
        <div class="toolbar-left">
            <div class="search-box">
                <i class="fa-regular fa-search"></i>
                <input
                    type="text"
                    pInputText
                    [(ngModel)]="searchTerm"
                    placeholder="Search modules/entities..."
                    class="search-input"
                    pTooltip="Filter modules and entities by name"
                    tooltipPosition="bottom">
            </div>
        </div>

        <div class="toolbar-center">
            <p-button
                icon="fa-regular fa-expand"
                pTooltip="Expand All"
                tooltipPosition="bottom"
                [text]="true"
                severity="secondary"
                (onClick)="expandAll()">
            </p-button>
            <p-button
                icon="fa-regular fa-compress"
                pTooltip="Collapse All"
                tooltipPosition="bottom"
                [text]="true"
                severity="secondary"
                (onClick)="collapseAll()">
            </p-button>

            <p-divider layout="vertical"></p-divider>

            <p-button
                icon="fa-regular fa-magnifying-glass-plus"
                pTooltip="Zoom In"
                tooltipPosition="bottom"
                [text]="true"
                severity="secondary"
                (onClick)="zoomIn()">
            </p-button>
            <span class="zoom-level">{{ (zoom * 100).toFixed(0) }}%</span>
            <p-button
                icon="fa-regular fa-magnifying-glass-minus"
                pTooltip="Zoom Out"
                tooltipPosition="bottom"
                [text]="true"
                severity="secondary"
                (onClick)="zoomOut()">
            </p-button>
            <p-button
                icon="fa-regular fa-arrows-to-circle"
                pTooltip="Fit to Viewport"
                tooltipPosition="bottom"
                [text]="true"
                severity="secondary"
                (onClick)="fitToViewport()">
            </p-button>
            <p-button
                icon="fa-regular fa-rotate-left"
                pTooltip="Reset View"
                tooltipPosition="bottom"
                [text]="true"
                severity="secondary"
                (onClick)="resetZoom()">
            </p-button>
        </div>

        <div class="toolbar-right">
            <div class="legend">
                <span class="legend-item">
                    <span class="legend-color" style="background: var(--green-500)"></span>
                    80%+
                </span>
                <span class="legend-item">
                    <span class="legend-color" style="background: var(--orange-500)"></span>
                    50-79%
                </span>
                <span class="legend-item">
                    <span class="legend-color" style="background: var(--red-500)"></span>
                    &lt;50%
                </span>
            </div>
        </div>
    </div>

    <!-- Canvas Container -->
    <div
        #canvasContainer
        class="canvas-container"
        [class.panning]="isPanning"
        (mousedown)="onCanvasMouseDown($event)"
        (mousemove)="onCanvasMouseMove($event)"
        (mouseup)="onCanvasMouseUp($event)"
        (mouseleave)="onCanvasMouseUp($event)">

        <!-- Loading Overlay -->
        @if (loading) {
            <div class="loading-overlay">
                <p-progressSpinner strokeWidth="4"></p-progressSpinner>
                <span>Loading coverage data...</span>
            </div>
        }

        <!-- SVG Canvas -->
        <svg
            #canvas
            class="canvas-svg"
            [attr.width]="canvasWidth"
            [attr.height]="canvasHeight"
            [style.transform]="getCanvasTransform()">

            <!-- Definitions -->
            <defs>
                <!-- Drop shadow filter -->
                <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.15"/>
                </filter>

                <!-- Hover shadow filter -->
                <filter id="hoverShadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="4" stdDeviation="6" flood-opacity="0.2"/>
                </filter>
            </defs>

            <!-- Connections -->
            <g class="connections-layer">
                @for (conn of connections; track conn.sourceId + conn.targetId) {
                    <path
                        class="connection-line"
                        [attr.d]="'M ' + conn.x1 + ' ' + conn.y1 + ' C ' + conn.x1 + ' ' + (conn.y1 + 20) + ', ' + (conn.x2 - 20) + ' ' + conn.y2 + ', ' + conn.x2 + ' ' + conn.y2"
                        fill="none"
                        stroke="var(--surface-border)"
                        stroke-width="2"/>
                }
            </g>

            <!-- Module Nodes -->
            <g class="nodes-layer">
                @for (node of nodes; track node.id) {
                    <!-- Module Node -->
                    <g
                        class="canvas-node module-node"
                        [class.expanded]="node.expanded"
                        [class.selected]="node.selected"
                        [class.hovered]="node.hovered"
                        [attr.transform]="'translate(' + node.x + ',' + node.y + ')'"
                        (click)="onNodeClick(node, $event)"
                        (mouseenter)="onNodeMouseEnter(node)"
                        (mouseleave)="onNodeMouseLeave(node)">

                        <!-- Background -->
                        <rect
                            class="node-bg"
                            [attr.width]="node.width"
                            [attr.height]="node.height"
                            rx="8"
                            [attr.fill]="getCoverageBgColor(node.coverage)"
                            filter="url(#dropShadow)"/>

                        <!-- Border -->
                        <rect
                            class="node-border"
                            [attr.width]="node.width"
                            [attr.height]="node.height"
                            rx="8"
                            fill="none"
                            [attr.stroke]="getCoverageColor(node.coverage)"
                            stroke-width="2"/>

                        <!-- Module Icon -->
                        <text
                            class="node-icon"
                            x="15"
                            [attr.y]="node.height / 2 - 5"
                            font-family="FontAwesome"
                            font-size="16"
                            [attr.fill]="getCoverageColor(node.coverage)">
                            &#xf1b3;
                        </text>

                        <!-- Module Label -->
                        <text
                            class="node-label"
                            x="40"
                            [attr.y]="node.height / 2 - 8"
                            font-size="13"
                            font-weight="600"
                            fill="var(--text-color)">
                            {{ node.sublabel }}
                        </text>

                        <!-- Module Name -->
                        <text
                            class="node-sublabel"
                            x="40"
                            [attr.y]="node.height / 2 + 8"
                            font-size="11"
                            fill="var(--text-color-secondary)">
                            {{ node.documented }}/{{ node.total }} entities
                        </text>

                        <!-- Coverage Badge -->
                        <g [attr.transform]="'translate(' + (node.width - 45) + ', 8)'">
                            <rect
                                width="37"
                                height="20"
                                rx="10"
                                [attr.fill]="getCoverageColor(node.coverage)"/>
                            <text
                                x="18.5"
                                y="14"
                                font-size="11"
                                font-weight="600"
                                fill="white"
                                text-anchor="middle">
                                {{ node.coverage }}%
                            </text>
                        </g>

                        <!-- Expand/Collapse Indicator -->
                        <g [attr.transform]="'translate(' + (node.width / 2 - 10) + ',' + (node.height - 5) + ')'">
                            <circle cx="10" cy="0" r="10" fill="var(--surface-card)" stroke="var(--surface-border)"/>
                            <text
                                x="10"
                                y="4"
                                font-family="FontAwesome"
                                font-size="10"
                                fill="var(--text-color-secondary)"
                                text-anchor="middle">
                                {{ node.expanded ? '&#xf077;' : '&#xf078;' }}
                            </text>
                        </g>

                        <!-- Progress Bar -->
                        <g [attr.transform]="'translate(10,' + (node.height - 18) + ')'">
                            <rect
                                [attr.width]="node.width - 20"
                                height="4"
                                rx="2"
                                fill="var(--surface-200)"/>
                            <rect
                                [attr.width]="(node.width - 20) * node.coverage / 100"
                                height="4"
                                rx="2"
                                [attr.fill]="getCoverageColor(node.coverage)"/>
                        </g>
                    </g>

                    <!-- Entity Child Nodes (when expanded) -->
                    @if (node.expanded && node.children) {
                        @for (entity of node.children; track entity.id) {
                            <g
                                class="canvas-node entity-node"
                                [class.selected]="entity.selected"
                                [class.hovered]="entity.hovered"
                                [class.documented]="$any(entity.data).isDocumented"
                                [attr.transform]="'translate(' + entity.x + ',' + entity.y + ')'"
                                (click)="onNodeClick(entity, $event)"
                                (dblclick)="onNodeDoubleClick(entity, $event)"
                                (mouseenter)="onNodeMouseEnter(entity)"
                                (mouseleave)="onNodeMouseLeave(entity)">

                                <!-- Background -->
                                <rect
                                    class="node-bg"
                                    [attr.width]="entity.width"
                                    [attr.height]="entity.height"
                                    rx="6"
                                    fill="var(--surface-card)"
                                    filter="url(#dropShadow)"/>

                                <!-- Border -->
                                <rect
                                    class="node-border"
                                    [attr.width]="entity.width"
                                    [attr.height]="entity.height"
                                    rx="6"
                                    fill="none"
                                    [attr.stroke]="getCoverageColor(entity.coverage)"
                                    stroke-width="1.5"/>

                                <!-- Status Icon -->
                                <text
                                    class="status-icon"
                                    x="12"
                                    [attr.y]="entity.height / 2 + 4"
                                    font-family="FontAwesome"
                                    font-size="14"
                                    [attr.fill]="getStatusColor($any(entity.data))">
                                    {{ $any(entity.data).isDocumented ? (entity.coverage === 100 ? '&#xf058;' : '&#xf111;') : '&#xf111;' }}
                                </text>

                                <!-- Entity Name -->
                                <text
                                    class="node-label"
                                    x="32"
                                    [attr.y]="entity.height / 2 - 5"
                                    font-size="12"
                                    font-weight="500"
                                    fill="var(--text-color)">
                                    {{ entity.label }}
                                </text>

                                <!-- Attribute Count -->
                                <text
                                    class="node-sublabel"
                                    x="32"
                                    [attr.y]="entity.height / 2 + 10"
                                    font-size="10"
                                    fill="var(--text-color-secondary)">
                                    {{ entity.sublabel }}
                                </text>

                                <!-- Coverage Mini Badge -->
                                <g [attr.transform]="'translate(' + (entity.width - 35) + ', ' + (entity.height / 2 - 8) + ')'">
                                    <rect
                                        width="28"
                                        height="16"
                                        rx="8"
                                        [attr.fill]="getCoverageBgColor(entity.coverage)"/>
                                    <text
                                        x="14"
                                        y="12"
                                        font-size="9"
                                        font-weight="600"
                                        [attr.fill]="getCoverageColor(entity.coverage)"
                                        text-anchor="middle">
                                        {{ entity.coverage }}%
                                    </text>
                                </g>
                            </g>
                        }
                    }
                }
            </g>
        </svg>
    </div>

    <!-- Empty State -->
    @if (!loading && nodes.length === 0) {
        <div class="empty-state">
            <i class="fa-regular fa-chart-pie"></i>
            <h3>No Coverage Data</h3>
            <p>Click "Refresh" to load coverage data from the backend.</p>
        </div>
    }
</div>
